import { createGlobalStyle } from 'styled-components';

/*
 * All sizes are based off units of 8 (px, %)
 * This allows for a consistent and predictable scale for adjustments
 * and avoids arbitrary measurements.
 * Exceptions are the base font size of 12px & the background alpha of 4%
 */

/*
 * Returns an hsla value based on the hex value of 181818 (dark grey)
 * To use, pass in the alpha percentage based on a unit of 8
 * 100 is full color
 * Examples: grey(8), grey(16) ... grey(100)
 */
const grey = percentage => {
    return `hsla(0, 0%, 9%, ${percentage / 100})`;
};

const GlobalStyle = createGlobalStyle`

    @font-face {
        font-family: 'Gilroy';
        src: url('https://dashboard.theconversioncloud.com/fonts/gilroy/gilroy-regular.woff') format('woff');
        font-style: normal;
        font-weight: 400;
    }

    @font-face {
        font-family: 'Gilroy';
        src: url('https://dashboard.theconversioncloud.com/fonts/gilroy/gilroy-bold.woff') format('woff');
        font-style: normal;
        font-weight: 900;
    }

    /* Global Variables */
    :root {
        /* Brand Palette */
        --brandPrimary: ${props => props.brandPrimary};
        --brandText: ${props => props.brandText};
        
        /* Visual Cues Palette */
        --error: hsl(357, 70%, 36%);
        --error-background: hsl(357, 70%, 96%);
        
        /* Decorative Styles */
        --borderRadius: 4px;
        --boxShadow: 0px 0px 16px 4px ${grey(32)};
        
        /* Fonts */
        --fontFamily: 'Gilroy', 'Segoe UI', Candara, 'Bitstream Vera Sans', 'DejaVu Sans', 'Bitstream Vera Sans',
        'Trebuchet MS', Verdana, 'Verdana Ref', sans-serif;
        --fontSize: 14px;
        --headingFont: normal 900 16px/24px var(--fontFamily);
    }
    
    /* Chatbot Styles */
    #clx-chatbot-dialog-window button {
        margin: 8px auto;
        width: 98% !important;
    }

    /* Modal Overlay */
    .clx__modal-overlay {
        background: ${grey(64)};
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
    }

    /* Global Button styles 
       Making this specific to combat customer site CSS conflicts
    */
    button {

        &.clx-module__launcher {
            border: none;
            font-weight: normal;
            text-transform: none;
            letter-spacing: normal;

            &.wallet {
                background: #fff !important;
                color: #222 !important;
            }
        }

        &.clx-step__button-nav,
        &.clx-step__button-back,
        &.clx-step__button-next {
            text-transform: initial !important;
            border: none;
            box-shadow: none;
        }

        &.clx-step__button-back {
            border: 1px solid hsla(0,0%,9%,0.24);
            color: hsla(0,0%,9%,1);

            &:hover {
                background: #fff;
                color: hsla(0,0%,9%,1);
            }
        }

        &.clx-step__button-next {
            margin: 0 0 0 auto;
        }

        &.clx-step__button-link {
            border: none;
            color: hsla(0,0%,9%,1);
        }

        &.notActive {
            font-weight: normal;
        }

    }

    /* App Step Wrapper (this is our main container) */
    .clx-step {
        background: #fff;
        border-radius: var(--borderRadius);
        box-shadow: var(--boxShadow);
        box-sizing: border-box;
        color: ${grey(100)};
        display: grid;
        font: normal var(--fontSize) var(--fontFamily);
        padding: 0;
        margin: 0 12px;
        width:320px;

        #clx-step__error-summary.visible {
            background: var(--error-background);
            border: 1px solid var(--error);
            border-radius: 4px;
            color: var(--error);
            margin: 8px auto;
            padding: 8px;
        }

        & *  {
            box-sizing: border-box;
            font-family: var(--fontFamily);
        }

        input[type="checkbox"]+label:not([for*="rating"])::before{
            content:none;
        }

        h1, h2, h3, h4, h5, h6 {
            color: ${grey(100)} !important;
            font-weight: 900 !important;
            line-height: 1 !important;
            text-transform: none !important; /* Using important for accessibility reasons. We DO NOT want uppercase here */
        }


        h1 {
            font-size: 2rem;
        }

        h2 {
            font-size: 1.75rem !important;
        }

        h3 {
            font-size: 1.25rem;
        }

        h4, h5, h6 {
            font-size: 1rem;
        }

        p {
            margin: 16px auto;
        }

        /* All labels */
        label {
            font-size: 1rem !important;
            text-transform: none !important; /* Using important for accessibility reasons. We DO NOT want uppercase here */
        }

        
        &__header,
        &__footer {
            max-width:320px;
            background: #ffffff !important;
            height: 64px;
            display: flex;
            align-items: center;
            padding: 0 16px;
            position: relative !important;
            justify-content:space-between !important;
            
        }
        
        /* Step Header */
        &__header {
            border: none;
            border-radius: var(--borderRadius) var(--borderRadius) 0 0;
            justify-content: space-between;
            padding: 0 16px;
            top:0px!important;
            flex-direction:unset !important;

            &-title {
                font: var(--headingFont);
                margin: 0 auto;
                text-align: center;
                text-transform: none !important; /* Using important for accessibility reasons. We DO NOT want uppercase here */
            }
        }
        
        /* Step Meter */
        &__meter {
            display: flex;
            align-content: space-around;
            height: 4px;
            max-width: 320px;
            width: 100%;
            
            span {
                background: ${grey(24)};
                margin-right: 2px;
                flex: 1;
                
                &.active,
                &.complete {
                    background: var(--brandPrimary);
                }
                
                &::last-of-type {
                    margin-right: 0;
                }
            }
        }
        
        &__intro {
            display: block;
            font-size: 16px;
            font-weight: 900;
            margin: 0 auto 16px;
            padding: 4px 0;
            text-transform: none !important; /* Using important for accessibility reasons. We DO NOT want uppercase here */
            width: 100%;
            
            small {
                display: block;
                font-size: small;
                font-weight: normal;
            }
        }
        
        /* Step Footer */
        &__footer {
            border-radius: 0 0 var(--borderRadius) var(--borderRadius);
            border-top: 1px solid ${grey(24)};
            justify-content: flex-end;
            margin-top: auto !important;
            padding:0px 2px !important;
            
            .clx-step__button-primary {
                font-size: 16px;
                margin-left: auto;
                
            }
            .clx-step__button-next {
                padding:16px 24px !important;
                margin-bottom:0px;
                letter-spacing: 0 !important;
            }
            .clx-step__button-back {
                font-size:12px;
                margin-right: auto;
                padding:16px 24px !important;
                margin-bottom:0px;
                letter-spacing: 0 !important;
            }
        }
        
        /* Layout formatting elements */
        &__content {
            background-color: ${grey(4)};
            width: 320px;
        }
        
        &__card,
        &__content {
            color: ${grey(100)};
            overflow-y: auto;
            text-align:left !important;
            padding: 8px !important;
            & h2{
                font-size: 24px !important;
            }
            & p {
                font-size: 16px 
            }
        }
        
        &__card {
            background: #ffffff;
            border-radius: var(--borderRadius);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            margin: auto;
            width: 98%;
        }
        
        &__form-label {
            font-size:14px !important;
        }
        &__form {
            background: none;
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            margin: 0 auto;
            padding: 0;
            min-width: 240px;
            max-width: 300px;
            width: 100%;

            label {
                background: none;
                border-radius: none;
                color: ${grey(100)};
                display: block !important;
                font-size: 1rem;
                line-height: 1.8rem;
                position: relative !important;
                top: 0;
                left: 0;
                padding: 0 !important;
                margin: 1rem 0 0 !important;
                text-transform: none !important; /* Using important for accessibility reasons. We DO NOT want uppercase here */

                &.full {
                    height: auto !important;
                }
            }

            input {
                font-size: 1rem;
                line-height: 1rem;
                letter-spacing: 0;

                &[type=checkbox] {
                    appearance: checkbox;
                    border: none;
                    display: inline;
                    padding: 0;
                    width: auto;
                    height: auto;
                }
            }
        }

        &__grid {
            display: grid;
            grid-column-gap: 8px;
            grid-row-gap: 8px;
            grid-template-columns: 1fr 1fr;
            grid-auto-rows: minmax(32px, 48px);
            width: 100%;
        }

        /* Links */
        &__anchor-button {
            background: var(--brandPrimary);
            border: none;
            border-radius: 4px;
            color: var(--brandText);
            cursor: pointer;
            display: block;
            font: 900 1rem var(--fontFamily);
            padding: 16px 32px;
            text-decoration: none;
        }

        /* Buttons */
        &__button,
        &__button-back,
        &__button-icon,
        &__button-link,
        &__button-nav,
        &__button-next,
        &__button-primary,
        &__button-radio,
        &__button-secondary {
            background: #fff;
            border: 1px solid ${grey(24)};
            border-radius: var(--borderRadius) !important;
            color: ${grey(100)};
            cursor: pointer;
            font: normal var(--fontSize) var(--fontFamily) !important;
            font-size: 1rem;
            padding: 16px 24px;

            &.selected {
                background: var(--brandPrimary) !important;
                color: var(--brandText) !important;
            }

            :active,
            :focus {
                outline: 5px auto Highlight;
                outline: 5px auto -webkit-focus-ring-color;
            }

            &.full {
                height: auto !important;
                width: 100%;
            }

            ::after,
            ::before {
                content: '' !important;
                display: none !important;
            }
        }

        &__button-radio {
            font-size: 1rem;
            padding: 16px 16px;
        }

        &__button-back {
            border-radius: var(--borderRadius) !important;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 0px !important;
            width: auto !important;

            svg {
                fill: none;
                margin-right: 4px;
                height: 16px;
                width: 16px;
            }
        }

        &__button-icon {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            margin: 4px auto;
            padding: 16px;

            .icon {
                background: #fff;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                height: 48px;
                width: 48px;
                flex: 0 0 48px;
                margin-right: 8px;
            }

            .content {
                align-self: flex-start;
                text-align: left;

                strong {
                    display: block;
                }
            }
        }

        &__button-link {
            background: none;
            border: none;
            padding: 8px 16px;
            text-decoration: underline;
        }

        &__button-nav {
            border: none;
            padding: 0 !important;
            display: flex;
            align-items: center;
            justify-content: center;
            outline: none;
            height: 24px !important;
            width: 24px !important;
            min-width: 0px !important;

            svg {
                cursor: pointer;
                fill: none;
                height: 16px;
                width: 16px;
            }
        }

        &__button-primary,
        &__button-next {
            font-size:16px;
            background: var(--brandPrimary) !important;
            border: none;
            border-radius: var(--borderRadius) !important;
            color: var(--brandText);
            font-weight: 900;
            min-width: 0px !important;
            width: auto !important;
        }

        /* Radio button is actually a label with a radio button inside */
        &__button-radio {
            display: flex;
            font-size: 1rem;
            font-weight: 400 !important;
            align-items: center;
            justify-content: center;
            z-index: 90;
            position: relative;

            input {
                border: none;
                cursor: pointer;
                display: block;
                position: absolute;
                height: 100%;
                width: 100%;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                opacity: 0.011;
                z-index: 100;
            }

            &.selected {
                background: ${props => props.backgroundColor};
                color: ${props => props.textColor};
            }
        }

        /* Input fields */
        &__checkbox {
            label {
                font-weight: 400 !important;
                font-size:14px !important;
                width:100% !important;
            }
        }

        &__input,
        &__select {
            flex: 0 1 100%;
            font-weight: 400 !important;
            margin-bottom: 16px;

            label {
                display: block;
                font-weight: 400 !important;
                margin-bottom: 4px;
            }

            &.half {
                flex: 0 1 48%;
            }

            input,
            select {
                background: #fff;
                border: 1px solid ${grey(32)};
                border-radius: 4px;
                display: block;
                padding: 8px;
                width: 100%;
                height:auto;
            }

            select {
                line-height: 1.3;
                margin: 0;
                box-shadow: 0 1px 0 1px rgba(0, 0, 0, 0.04);
                -moz-appearance: none;
                -webkit-appearance: none;
                appearance: none;
                background-color: #fff;
                background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpolyline fill='none' stroke='%23545454' stroke-linecap='round' stroke-linejoin='round' stroke-width='2px' points='1 1 10 10 19 1'/%3E%3C/svg%3E"),
                    linear-gradient(to bottom, #ffffff 0%, #ffffff 100%);
                background-repeat: no-repeat, repeat;
                background-position: right 8px top 64%, 0 0;
                background-size: 16px auto, 100%;
            }
            /* Hide arrow icon in IE browsers */
            select::-ms-expand {
                display: none;
            }
            /* Set options to normal weight */
            select option {
                font-weight: normal;
            }
            /* Disabled styles */
            select:disabled,
            select[aria-disabled='true'] {
                color: graytext;
                background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpolyline fill='none' stroke='%23545454' stroke-linecap='round' stroke-linejoin='round' stroke-width='2px' points='1 1 10 10 19 1'/%3E%3C/svg%3E"),
                    linear-gradient(to bottom, #f7f7f7 0%, #f7f7f7 100%);
            }
            select:disabled:hover,
            select[aria-disabled='true'] {
                border-color: #aaa;
            }
            
            input.invalid,
            select.invalid {
                border: 1px solid var(--error);
            }

            .validation {
                display: block;
                color: var(--error);
                font-size: 12px;
                margin: 4px 0;
            }
        }

        /* Design Elements */
        &__badge {
            background: var(--brandPrimary);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 64px;
            width: 64px;
            margin: 8px auto 16px;

            svg {
                fill: none;
                height: 32px;
                width: 32px;
            }
        }

        &__separator {
            background: ${grey(8)};
            border: 0;
            height: 1px;
            margin: 16px auto;

            :before {
                display: none;
            }

            :after {
                display: none;
            }
        }

        .clx-confirmation {
            width: 100%;
        }

        /* Chat elements */
        .clx-chatbot-bubble {
            background: rgba(0,0,0,0.05);
            border-radius: 8px;
            margin: 8px auto;
            padding: 8px;

            p {
                margin: 0 auto;
            }

            .clx-chatbot-bubble-typing {
                position: relative;
                left: 24px;
                width: 8px;
                height: 8px;
                border-radius: 4px;
                background-color: #ccc;
                color: #ccc;
                animation: typingDots 1s infinite linear alternate;
                animation-delay: .5s;

                &::before,
                &::after {
                    content: '';
                    display: inline-block;
                    position: absolute;
                    top: 0;
                    width: 8px;
                    height: 8px;
                    border-radius: 4px;
                    background-color: #ccc;
                    color: #ccc;
                    animation: typingDots 1s infinite alternate;
                }

                &::before {
                    left: -16px;
                     animation-delay: 0s;
                }

                &::after {
                    left: 16px;
                     animation-delay: 1s;
                }
            }

            @keyframes typingDots {
                0% {
                    background-color: #ccc;
                }
                50%,
                100% {
                    background-color: #222;
                }
            }
        }
    }
`;

export default GlobalStyle;
